import "primeflex/primeflex.css";
import "./DashboardPage.css";

import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

import { AppDispatch, RootState } from "../../redux/store/store";
import { fetchRTUs } from "../../redux/slices/rtuSlice";
import { FEATURE_FLAGS } from "../../config/featureFlags";
import { DeviceLocationPieChart } from "./DeviceLocationPieChart";
import { DevicesByClient } from "./DevicesByClient";
import FirmwareVersionPieChart from "./FirmwareVersionPieChart";
import ImageVersionDistribution from "./ImageVersionDistribution";
import { LowDiskStorageDevices } from "./LowDiskStorageDevices";
import { SkeletonChartPanel, SkeletonKpiRail, SkeletonRegion } from "../common/Skeleton/Skeleton";
import ReachabilityCard from "./ReachabilityCard";
import StatusHistoryChart from "./StatusHistoryChart";
import { TotalDevices } from "./TotalDevices";
import { DashboardDistributionCardHeader } from "./DashboardDistributionCardHeader";
import { usePersistedDistributionView } from "../../hooks/usePersistedDistributionView";
import {
  DEFAULT_LOW_STORAGE_TRIGGER_PERCENT,
  getDashboardDefaults,
} from "../../services/dashboardSettingsService";

export const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { rtus, loading, error } = useSelector(
    (state: RootState) => state.rtu || {}
  );
  const dispatch = useDispatch<AppDispatch>();
  const [softwareViewMode, setSoftwareViewMode] =
    usePersistedDistributionView("firmware", "bars");
  const [locationViewMode, setLocationViewMode] =
    usePersistedDistributionView("deviceLocation", "bars");
  const [lowStorageTriggerPercent, setLowStorageTriggerPercent] = useState(
    DEFAULT_LOW_STORAGE_TRIGGER_PERCENT
  );
  const lowStorageFreePercent = Math.max(0, 100 - lowStorageTriggerPercent);
  const lowStorageInfo = `Shows RTUs with less than ${lowStorageFreePercent}% free disk space. Current threshold: disk usage at or above ${lowStorageTriggerPercent}%.`;
  const hasRtus = Array.isArray(rtus) && rtus.length > 0;
  const rtuFleetLoading = Boolean(loading) && !hasRtus;
  // Status history and reachability fetch their own endpoints; they must not
  // wait for GET /rtu/all. Remaining cards are derived from the RTU list.
  const independentInsightsEnabled = FEATURE_FLAGS.historyChart;
  const showRtuDependentCards = hasRtus || rtuFleetLoading;

  useEffect(() => {
    dispatch(fetchRTUs({}));
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    getDashboardDefaults()
      .then((defaults) => {
        if (!cancelled && Number.isFinite(defaults.lowStorageTriggerPercent)) {
          setLowStorageTriggerPercent(defaults.lowStorageTriggerPercent);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLowStorageTriggerPercent(DEFAULT_LOW_STORAGE_TRIGGER_PERCENT);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rtuDependentBody = (ready: ReactNode) => {
    if (hasRtus) {
      return ready;
    }
    return (
      <SkeletonRegion label={t('common.loading', { defaultValue: 'Loading' })}>
        <SkeletonChartPanel height="9rem" />
      </SkeletonRegion>
    );
  };

  if (!independentInsightsEnabled && rtuFleetLoading) {
    return (
      <div className="grid-container dashboard-page">
        <div className="grid custom-grid-col">
          <div className="col-12">
            <Card className="dashboard-card dashboard-history-card flat-card" title={t("dashboard.page.cards.statusHistory")}>
              <SkeletonRegion label={t('dashboard.history.loading', { defaultValue: 'Loading device status history...' })}>
                <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 14rem', minWidth: '12rem' }}>
                    <SkeletonKpiRail rows={4} />
                  </div>
                  <div style={{ flex: '2 1 20rem', minWidth: '16rem' }}>
                    <SkeletonChartPanel />
                  </div>
                </div>
              </SkeletonRegion>
            </Card>
          </div>
          {Array.from({ length: 4 }).map((_, index) => (
            <div className="col-12 md:col-6 lg:col-6" key={index}>
              <Card className="dashboard-card flat-card">
                <SkeletonRegion label={t('common.loading', { defaultValue: 'Loading' })}>
                  <SkeletonChartPanel height="9rem" />
                </SkeletonRegion>
              </Card>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!independentInsightsEnabled && !hasRtus) {
    const title = error
      ? t("dashboard.page.states.unavailableTitle")
      : t("dashboard.page.states.emptyTitle");
    const body = error
      ? t("dashboard.page.states.unavailableBody")
      : t("dashboard.page.states.emptyBody");

    return (
      <div
        className={`dashboard-state${error ? " dashboard-state--error" : ""}`}
        role={error ? "alert" : "status"}
      >
        <h2 className="dashboard-state__title">{title}</h2>
        <p className="dashboard-state__body">{body}</p>
      </div>
    );
  }

  const fleetUnavailableBanner = independentInsightsEnabled && !hasRtus && !rtuFleetLoading ? (
    <div className="col-12">
      <div
        className={`dashboard-state${error ? " dashboard-state--error" : ""}`}
        role={error ? "alert" : "status"}
      >
        <h2 className="dashboard-state__title">
          {error
            ? t("dashboard.page.states.unavailableTitle")
            : t("dashboard.page.states.emptyTitle")}
        </h2>
        <p className="dashboard-state__body">
          {error
            ? t("dashboard.page.states.unavailableBody")
            : t("dashboard.page.states.emptyBody")}
        </p>
      </div>
    </div>
  ) : null;

  return (
    <div className="grid-container dashboard-page">
      <div className="grid custom-grid-col">
        {FEATURE_FLAGS.historyChart ? (
          <div className="col-12">
            <Card
              className="dashboard-card dashboard-history-card flat-card"
              title={t("dashboard.page.cards.statusHistory")}
            >
              <StatusHistoryChart rtus={rtus} />
            </Card>
          </div>
        ) : null}
        {!FEATURE_FLAGS.historyChart ? (
          <div className="col-12 md:col-6 lg:col-6">
            <Card
              className="dashboard-card flat-card dashboard-card--distribution-toggle"
              title={
                <DashboardDistributionCardHeader
                  key="distribution-header-firmware"
                  title={t("dashboard.page.cards.softwareVersions")}
                  mode={softwareViewMode}
                  onModeChange={setSoftwareViewMode}
                  instanceId="firmware"
                />
              }
            >
              {rtuDependentBody(
                <FirmwareVersionPieChart
                  rtus={rtus}
                  viewMode={softwareViewMode}
                />
              )}
            </Card>
          </div>
        ) : null}
        {showRtuDependentCards ? (
          <div className="col-12 md:col-6 lg:col-6">
            <Card
              className="dashboard-card flat-card"
              title={
                <span className="dashboard-card-title-with-meta">
                  <span>{t("dashboard.page.cards.storageAttention")}</span>
                  <span className="dashboard-card-title-with-meta__actions">
                    <Tooltip title={lowStorageInfo} arrow>
                      <IconButton
                        size="small"
                        aria-label="Storage Attention threshold information"
                      >
                        <InfoOutlinedIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Storage Attention settings" arrow>
                      <IconButton
                        size="small"
                        aria-label="Open Storage Attention settings"
                        onClick={() => navigate('/admin/settings/system-defaults/storage-attention?highlight=storage-attention')}
                      >
                        <SettingsOutlinedIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </span>
                </span>
              }
            >
              {rtuDependentBody(
                <LowDiskStorageDevices rtus={rtus} thresholdPercent={lowStorageTriggerPercent} />
              )}
            </Card>
          </div>
        ) : null}
        {FEATURE_FLAGS.historyChart && showRtuDependentCards ? (
          <div className="col-12 md:col-6 lg:col-6">
            <Card
              className="dashboard-card flat-card dashboard-card--distribution-toggle"
              title={
                <DashboardDistributionCardHeader
                  key="distribution-header-firmware"
                  title={t("dashboard.page.cards.softwareVersions")}
                  mode={softwareViewMode}
                  onModeChange={setSoftwareViewMode}
                  instanceId="firmware"
                />
              }
            >
              {rtuDependentBody(
                <FirmwareVersionPieChart
                  rtus={rtus}
                  viewMode={softwareViewMode}
                />
              )}
            </Card>
          </div>
        ) : null}
        {FEATURE_FLAGS.historyChart && showRtuDependentCards ? (
          <div className="col-12 md:col-6 lg:col-6">
            <Card
              className="dashboard-card flat-card"
              title={t("dashboard.page.cards.imageVersions", {
                defaultValue: "Image Version Distribution",
              })}
            >
              {rtuDependentBody(<ImageVersionDistribution rtus={rtus} />)}
            </Card>
          </div>
        ) : null}
        {FEATURE_FLAGS.historyChart ? (
          <div className="col-12 md:col-6 lg:col-6">
            <Card
              className="dashboard-card dashboard-reachability-card flat-card"
              title={t("dashboard.page.cards.reachability")}
            >
              <ReachabilityCard />
            </Card>
          </div>
        ) : (
          <>
            {showRtuDependentCards ? (
              <div className="col-12 md:col-6 lg:col-6">
                <Card
                  className="dashboard-card flat-card"
                  title={t("dashboard.page.cards.deviceAvailability")}
                >
                  {rtuDependentBody(<TotalDevices rtus={rtus} />)}
                </Card>
              </div>
            ) : null}
          </>
        )}
        {showRtuDependentCards ? (
          <div className="col-12 md:col-6 lg:col-6">
            <Card
              className="dashboard-card flat-card"
              title={t("dashboard.page.cards.devicesByProducer")}
            >
              {rtuDependentBody(<DevicesByClient rtus={rtus} />)}
            </Card>
          </div>
        ) : null}
        {showRtuDependentCards ? (
          <div className="col-12 md:col-6 lg:col-6">
            <Card
              className="dashboard-card flat-card dashboard-card--distribution-toggle"
              title={
                <DashboardDistributionCardHeader
                  key="distribution-header-deviceLocation"
                  title={t("dashboard.page.cards.deviceLocations")}
                  mode={locationViewMode}
                  onModeChange={setLocationViewMode}
                  instanceId="deviceLocation"
                />
              }
            >
              {rtuDependentBody(
                <DeviceLocationPieChart
                  rtus={rtus}
                  viewMode={locationViewMode}
                />
              )}
            </Card>
          </div>
        ) : null}
        {fleetUnavailableBanner}
      </div>
    </div>
  );
};
